import { useCopyToClipboard } from 'hooks/useCopyToClipboard'
import { trackEvent } from 'lib/utils/analytics'
import React, { FunctionComponent } from 'react'
import { Facebook, Linkedin, Twitter } from 'react-feather'
import Whatsapp from './icons/social-icons/whatsapp.svg'
import Telegram from './icons/social-icons/telegram.svg'
import Hackernews from './icons/social-icons/hackernews.svg'
import {
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share'

interface IShareArticleProps {
  url: string
  title: string
  coverUrl?: string
}

type Method = 'Whatsapp' | 'Twitter' | 'Facebook' | 'Linkedin' | 'Telegram' | 'Hackernews' | 'Copy'

const ShareArticle: FunctionComponent<IShareArticleProps> = ({ url, title }): JSX.Element => {
  const [isCopied, handleCopy] = useCopyToClipboard(1500, url)
  const hackernewsShareUrl = `https://news.ycombinator.com/submitlink?t=${title}&u=${url}`

  const onShareClick = (method: Method) => {
    trackEvent({ name: 'Article Shared', data: { method, url } })

    if (method === 'Copy') {
      handleCopy()
    }
  }

  return (
    <div className="text-sm text-gray-700 dark:text-gray-300 flex flex-wrap flex-col mt-6 lg:mt-0 sm:flex-row lg:flex-col items-center justify-center gap-4">
      <h3 className="font-bold uppercase">Share with others</h3>
      <div className="m-0 flex items-center justify-center gap-4">
        <WhatsappShareButton
          url={url + '?utm_source=WhatsApp'}
          title={title}
          separator=" | "
          onClick={() => onShareClick('Whatsapp')}
          className="transform stroke-current transition hover:rotate-[-6deg]"
          aria-label="Share on WhatsApp"
        >
          <Whatsapp />
        </WhatsappShareButton>
        <TelegramShareButton
          title={title}
          url={url + '?utm_source=Telegram'}
          onClick={() => onShareClick('Telegram')}
          className="transform stroke-current transition hover:rotate-[-6deg]"
          aria-label="Share on Telegram"
        >
          <Telegram />
        </TelegramShareButton>
        <FacebookShareButton
          url={url + '?utm_source=Facebook'}
          onClick={() => onShareClick('Facebook')}
          className="transform stroke-current transition hover:rotate-[-6deg]"
          aria-label="Share on Facebook"
        >
          <Facebook />
        </FacebookShareButton>
        <TwitterShareButton
          url={url + '?utm_source=Twitter'}
          title={title}
          onClick={() => onShareClick('Twitter')}
          className="transform stroke-current transition hover:rotate-[-6deg]"
          aria-label="Share on Twitter"
        >
          <Twitter />
        </TwitterShareButton>
        <LinkedinShareButton
          url={url + '?utm_source=Linkedin'}
          title={title}
          onClick={() => onShareClick('Linkedin')}
          className="transform stroke-current transition hover:rotate-[-6deg]"
          aria-label="Share on Linkedin"
        >
          <Linkedin />
        </LinkedinShareButton>
        <a
          href={hackernewsShareUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onShareClick('Hackernews')}
          className="transform stroke-current transition hover:rotate-[-6deg]"
          aria-label="Share on Hacker News"
        >
          <Hackernews />
        </a>
        <button
          aria-label="Copy url to clipboard"
          className={`group -ml-3 ${isCopied ? 'text-primary-500' : ''}`}
          onClick={() => onShareClick('Copy')}
        >
          <span className="sr-only">Copy url</span>
          <svg
            aria-hidden="true"
            viewBox="0 0 32 32"
            fill="none"
            className="h-11 w-11 transform stroke-current transition group-hover:rotate-[-6deg]"
          >
            <path
              d="M12.9975 10.7499L11.7475 10.7499C10.6429 10.7499 9.74747 11.6453 9.74747 12.7499L9.74747 21.2499C9.74747 22.3544 10.6429 23.2499 11.7475 23.2499L20.2475 23.2499C21.352 23.2499 22.2475 22.3544 22.2475 21.2499L22.2475 12.7499C22.2475 11.6453 21.352 10.7499 20.2475 10.7499L18.9975 10.7499"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
            <path
              d="M17.9975 12.2499L13.9975 12.2499C13.4452 12.2499 12.9975 11.8022 12.9975 11.2499L12.9975 9.74988C12.9975 9.19759 13.4452 8.74988 13.9975 8.74988L17.9975 8.74988C18.5498 8.74988 18.9975 9.19759 18.9975 9.74988L18.9975 11.2499C18.9975 11.8022 18.5498 12.2499 17.9975 12.2499Z"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
            <path
              d="M13.7475 16.2499L18.2475 16.2499"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
            <path
              d="M13.7475 19.2499L18.2475 19.2499"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
            <g
              className={`transition-opacity ${
                isCopied ? 'rotate-[-6deg] transform opacity-100 transition' : 'opacity-0'
              }`}
            >
              <path
                d="M15.9975 5.99988L15.9975 3.99988"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M19.9975 5.99988L20.9975 4.99988"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M11.9975 5.99988L10.9975 4.99988"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </g>
          </svg>
        </button>
      </div>
    </div>
  )
}

export default ShareArticle
